import { AUDIO_ASSETS, SFX_DURATION_MS, SFX_MIX, SFX_RATE } from "./audioAssets";

const MASTER_GAIN = 0.72;
const MUSIC_GAIN = 3;
const SHOT_THROTTLE_MS = 34;
const HOME_MODE = "home";
const GAMEPLAY_MODE = "gameplay";

const shuffleTracks = (tracks = []) => {
	const next = [...tracks];
	for (let index = next.length - 1; index > 0; index -= 1) {
		const swapIndex = Math.floor(Math.random() * (index + 1));
		[next[index], next[swapIndex]] = [next[swapIndex], next[index]];
	}
	return next;
};

const createContext = () => {
	const AudioContextClass = window.AudioContext || window.webkitAudioContext;
	return AudioContextClass ? new AudioContextClass() : null;
};

const clamp = (value, min = 0, max = 1) => Math.min(max, Math.max(min, value));

const browserHasFocus = () => {
	if (typeof document === "undefined") return true;
	const visible = document.visibilityState !== "hidden";
	const focused = typeof document.hasFocus !== "function" || document.hasFocus();
	return visible && focused;
};

const createMediaElement = (src) => {
	if (typeof Audio === "undefined") return null;
	const audio = new Audio(src);
	audio.preload = "auto";
	audio.loop = false;
	audio.volume = 1;
	return audio;
};

export const createSoundService = (settingsRef) => {
	let ctx = null;
	let master = null;
	let musicBus = null;
	let activeMusicKey = null;
	let currentMusicMode = HOME_MODE;
	let gameplayTrackKey = null;
	let gameplayQueue = [];
	let unlocked = false;
	let musicPreloaded = false;
	let nativeActive = true;
	let nativeAppStateHandle = null;
	let bootUnlockArmed = false;
	let disposed = false;
	let unlockFromGesture = null;
	let lastShotAt = 0;
	let activeSamples = new Set();
	const sampleCache = new Map();
	const musicElements = new Map();
	const musicSources = new Map();

	const getSfxEnabled = () => settingsRef.current?.sfx ?? settingsRef.current?.sound ?? true;
	const getSfxVolume = () => clamp(Number(settingsRef.current?.sfxVolume ?? 0.8));
	const getMusicVolume = () => clamp(Number(settingsRef.current?.musicVolume ?? 0.55));
	const isAppFocused = () => nativeActive && browserHasFocus();
	const isMusicEnabled = () => {
		const settings = settingsRef.current;
		return Boolean(settings?.music && !settings.performanceMode);
	};

	const ensureContext = () => {
		if (ctx || typeof window === "undefined") return ctx;
		ctx = createContext();
		if (!ctx) return null;

		master = ctx.createGain();
		master.gain.value = MASTER_GAIN;
		master.connect(ctx.destination);

		musicBus = ctx.createGain();
		musicBus.gain.value = 0;
		musicBus.connect(master);

		return ctx;
	};

	const getMusicElement = (track) => {
		const audio = ensureContext();
		if (!audio || !musicBus || !track) return null;
		if (!musicElements.has(track.id)) {
			const element = createMediaElement(track.src);
			if (!element) return null;
			element.addEventListener("ended", () => {
				if (track.id !== activeMusicKey) return;
				activeMusicKey = null;
				if (track.id === gameplayTrackKey) gameplayTrackKey = null;
				startMusic();
			});
			musicElements.set(track.id, element);
		}

		const element = musicElements.get(track.id);
		if (!musicSources.has(track.id)) {
			const source = audio.createMediaElementSource(element);
			source.connect(musicBus);
			musicSources.set(track.id, source);
		}
		return element;
	};

	const refillGameplayQueue = () => {
		gameplayQueue = shuffleTracks(AUDIO_ASSETS.gameplayMusic);
		if (gameplayTrackKey && gameplayQueue.length > 1 && gameplayQueue[0]?.id === gameplayTrackKey) {
			gameplayQueue.push(gameplayQueue.shift());
		}
	};

	const getTrackById = (trackId) => AUDIO_ASSETS.gameplayMusic.find((track) => track.id === trackId) || null;

	const getCurrentTrack = () => {
		if (currentMusicMode !== GAMEPLAY_MODE) return AUDIO_ASSETS.homeMusic;
		if (gameplayTrackKey) return getTrackById(gameplayTrackKey);
		if (!gameplayQueue.length) refillGameplayQueue();
		const nextTrack = gameplayQueue.shift() || AUDIO_ASSETS.gameplayMusic[0];
		gameplayTrackKey = nextTrack?.id || null;
		return nextTrack;
	};

	const updateMusicLevels = () => {
		const volume = isMusicEnabled() ? getMusicVolume() : 0;
		if (musicBus && ctx) musicBus.gain.setTargetAtTime(MUSIC_GAIN, ctx.currentTime, 0.04);
		for (const element of musicElements.values()) element.volume = volume;
	};

	const getSample = (cueName) => {
		const src = AUDIO_ASSETS.sfx[cueName] || AUDIO_ASSETS.sfx.ui;
		if (!sampleCache.has(src)) sampleCache.set(src, createMediaElement(src));
		return sampleCache.get(src);
	};

	const stopActiveSamples = () => {
		for (const sample of activeSamples) {
			sample.pause();
			sample.currentTime = 0;
		}
		activeSamples.clear();
	};

	const pauseAllMusic = () => {
		if (musicBus && ctx) {
			musicBus.gain.cancelScheduledValues(ctx.currentTime);
			musicBus.gain.setTargetAtTime(MUSIC_GAIN, ctx.currentTime, 0.02);
		}
		for (const element of musicElements.values()) element.pause();
	};

	const pauseOtherMusic = (trackId) => {
		for (const [id, element] of musicElements.entries()) {
			if (id !== trackId) element.pause();
		}
	};

	const startMusic = () => {
		if (disposed) return;
		const settings = settingsRef.current;
		if (!settings?.music || settings.performanceMode || !isAppFocused()) {
			pauseAllMusic();
			return;
		}

		const audio = ensureContext();
		if (!audio || audio.state !== "running") return;

		const track = getCurrentTrack();
		const nextElement = getMusicElement(track);
		if (!nextElement) return;

		pauseOtherMusic(track.id);
		activeMusicKey = track.id;
		if (nextElement.ended) nextElement.currentTime = 0;
		if (!nextElement.paused && !nextElement.ended) {
			updateMusicLevels();
			return;
		}

		updateMusicLevels();
		nextElement.play().catch(() => {
			// Browser autoplay rules can still reject resume after unusual focus changes.
		});
	};

	const setMusicMode = (mode = HOME_MODE) => {
		const nextMode = mode === GAMEPLAY_MODE ? GAMEPLAY_MODE : HOME_MODE;
		if (currentMusicMode === nextMode) {
			syncSettings();
			return;
		}

		currentMusicMode = nextMode;
		syncSettings();
	};

	const syncSettings = () => {
		if (!ctx || !master) return;
		master.gain.setTargetAtTime(MASTER_GAIN, ctx.currentTime, 0.04);
		updateMusicLevels();
		startMusic();
	};

	const handleFocusChange = () => {
		if (!isAppFocused()) {
			pauseAllMusic();
			stopActiveSamples();
			return;
		}
		if (unlocked) syncSettings();
	};

	const unlock = async () => {
		if (disposed) return false;
		const audio = ensureContext();
		if (!audio) return false;
		try {
			if (audio.state === "suspended") await audio.resume();
		} catch {
			return false;
		}
		if (audio.state !== "running") return false;
		unlocked = true;

		if (!musicPreloaded) {
			getMusicElement(AUDIO_ASSETS.homeMusic)?.load();
			for (const track of AUDIO_ASSETS.gameplayMusic) getMusicElement(track)?.load();
			for (const cueName of Object.keys(AUDIO_ASSETS.sfx)) getSample(cueName)?.load();
			musicPreloaded = true;
		}

		syncSettings();
		return true;
	};

	const armBootUnlock = () => {
		if (bootUnlockArmed || typeof window === "undefined") return;
		bootUnlockArmed = true;

		unlockFromGesture = () => {
			unlock().catch(() => {});
		};

		window.addEventListener("pointerdown", unlockFromGesture, { once: true, capture: true });
		window.addEventListener("keydown", unlockFromGesture, { once: true, capture: true });
		window.addEventListener("touchstart", unlockFromGesture, { once: true, capture: true, passive: true });
	};

	if (typeof window !== "undefined") {
		window.addEventListener("focus", handleFocusChange);
		window.addEventListener("blur", handleFocusChange);
		window.addEventListener("pagehide", handleFocusChange);
		document?.addEventListener("visibilitychange", handleFocusChange);
		import("@capacitor/app").then(({ App }) => {
			App.addListener("appStateChange", ({ isActive }) => {
				if (disposed) return;
				nativeActive = isActive;
				handleFocusChange();
			}).then((handle) => {
				if (disposed) {
					handle.remove();
					return;
				}
				nativeAppStateHandle = handle;
			});
		}).catch(() => {
			nativeActive = true;
		});
		armBootUnlock();
	}

	const play = (cueName = "ui", intensity = 1) => {
		const settings = settingsRef.current;
		if (!getSfxEnabled() || !isAppFocused() || (settings.performanceMode && cueName.toLowerCase().includes("hit"))) return;

		const nowMs = performance.now();
		if ((cueName === "shot" || cueName === "hit") && nowMs - lastShotAt < SHOT_THROTTLE_MS) return;
		if (cueName === "shot" || cueName === "hit") lastShotAt = nowMs;

		const source = getSample(cueName);
		if (!source) return;

		const sample = source.cloneNode(true);
		const mix = SFX_MIX[cueName] ?? SFX_MIX.ui ?? 0.4;
		sample.volume = clamp(mix * intensity * getSfxVolume());
		sample.playbackRate = SFX_RATE[cueName] ?? 1;
		sample.preservesPitch = true;
		const maxDuration = SFX_DURATION_MS[cueName];
		let cutoffTimer = null;
		sample.addEventListener("ended", () => activeSamples.delete(sample), { once: true });
		sample.addEventListener("error", () => activeSamples.delete(sample), { once: true });
		if (maxDuration) {
			cutoffTimer = window.setTimeout(() => {
				sample.pause();
				sample.currentTime = 0;
				activeSamples.delete(sample);
			}, maxDuration);
			sample.addEventListener("ended", () => window.clearTimeout(cutoffTimer), { once: true });
			sample.addEventListener("error", () => window.clearTimeout(cutoffTimer), { once: true });
		}
		activeSamples.add(sample);
		sample.play().catch(() => activeSamples.delete(sample));
	};

	const flush = (cues = []) => {
		if (!cues.length) return;
		for (const cue of cues.slice(0, 16)) play(cue.name, cue.intensity);
	};

	return {
		unlock,
		play,
		flush,
		syncSettings,
		setMusicMode,
		dispose() {
			disposed = true;
			pauseAllMusic();
			stopActiveSamples();
			if (typeof window !== "undefined") {
				window.removeEventListener("focus", handleFocusChange);
				window.removeEventListener("blur", handleFocusChange);
				window.removeEventListener("pagehide", handleFocusChange);
				document?.removeEventListener("visibilitychange", handleFocusChange);
				if (unlockFromGesture) {
					window.removeEventListener("pointerdown", unlockFromGesture, { capture: true });
					window.removeEventListener("keydown", unlockFromGesture, { capture: true });
					window.removeEventListener("touchstart", unlockFromGesture, { capture: true });
				}
			}
			nativeAppStateHandle?.remove();
			for (const source of musicSources.values()) source.disconnect();
			musicBus?.disconnect();
			master?.disconnect();
			ctx?.close();
			ctx = null;
			master = null;
			musicBus = null;
			activeMusicKey = null;
			gameplayTrackKey = null;
			gameplayQueue = [];
			activeSamples = new Set();
			musicElements.clear();
			musicSources.clear();
			sampleCache.clear();
		},
		get unlocked() {
			return unlocked;
		},
	};
};
